import { Form, Modal, Select } from 'antd';
import { IDocumentTemplate } from 'interfaces/documentation/document-template';
import { actionableErrorMessage } from 'utils/actionable-error-message';
import { useCallback, useMemo } from 'react';
import { useCreate } from '@refinedev/core';
import { useDocumentationStore } from '../documentation-store';
import { useParams } from 'react-router-dom';

interface ModalSetKindProps {
  templates: IDocumentTemplate[];
}

export const ModalSetKind = ({ templates }: ModalSetKindProps) => {
  const [form] = Form.useForm();
  const { id } = useParams();
  const { mutateAsync: updateTemplate } = useCreate();
  const modal = useDocumentationStore(s => s.modal);
  const editDocument = useDocumentationStore(s => s.editDocument);
  const queryHandler = useDocumentationStore(s => s.queryHandler);
  const closeModal = useDocumentationStore(s => s.closeModal);

  const options = useMemo(() => {
    return templates.length > 0
      ? templates.map(template => ({
          label: template.name,
          value: template.id
        }))
      : [];
  }, [templates]);

  const handleOk = useCallback(() => {
    if (!editDocument) {
      return null;
    }

    const templateId = form.getFieldValue('template_id');
    const resource = `documentations/${id}/documents/${editDocument.id}/classify`;

    updateTemplate({
      resource,
      values: { template_id: templateId },
      errorNotification: error => {
        // O interceptor do api.ts espalha o corpo da resposta no próprio erro.
        const errorCode = error?.code;

        if (errorCode === 'documentations_template_already_fulfilled_error') {
          return {
            type: 'error',
            description: 'Não foi possível categorizar o documento',
            message:
              'Já existe um documento pendente ou aprovado nesta categoria'
          };
        }
        return {
          type: 'error',
          message: actionableErrorMessage(
            error,
            'Ocorreu um erro interno no serviço'
          ),
          description: 'Erro ao categorizar documento!'
        };
      },
      successNotification: () => {
        const selectedOption = options.find(
          e => e.value === form.getFieldValue('template_id')
        );

        return {
          type: 'success',
          description: 'Categoria alterada com sucesso',
          message: `A categoria do documento foi alterada para "${selectedOption?.label}"`
        };
      }
    })
      .then(() => {
        form.resetFields();
        queryHandler();
        closeModal();
      })
      .catch(() => null);
  }, [
    closeModal,
    editDocument,
    form,
    id,
    options,
    queryHandler,
    updateTemplate
  ]);

  return (
    <Modal
      visible={modal === 'setKind'}
      title={
        editDocument?.template_id
          ? 'Trocar categoria do documento'
          : 'Categorizar documento'
      }
      width={400}
      okText="Salvar"
      onCancel={() => {
        form.resetFields();
        closeModal();
      }}
      onOk={() => handleOk()}
    >
      <Form form={form} layout="vertical">
        <Form.Item name="template_id" label="Tipo de documento">
          <Select
            key={editDocument?.template_id}
            options={options.filter(
              option => option.value !== editDocument?.template_id
            )}
            placeholder="Selecione o tipo"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
